import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { ContextActions } from '../components/ContextActions.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { CopyableCode, COPYABLE_CODE_CSS } from '../components/CopyableCode.jsx';
import { useToast } from '../components/Toast.jsx';
import { Wizard, WizardStep } from '../components/Wizard.jsx';
import {
  DeleteIcon,
  EditIcon,
  RemoveIcon,
  SaveIcon,
  XIcon,
  SendIcon,
  CopyIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  AlertTriangleIcon,
} from '../components/icons';

/**
 * Team + invites management.
 *
 * Owners and Admins see the full list of admin users and outstanding
 * invites; they can mint new invites with a role attached, and revoke
 * invites that haven't been accepted.  Editors and below see a read-
 * only member list.
 *
 * No custom RBAC ceremony — the four roles are Owner / Admin / Editor
 * / Viewer.  See docs/rfcs/rfc-025-tenant-team-and-audience.md.
 */

const ROLES = [
  { id: 'owner', label: 'Owner', blurb: 'Everything, including billing.' },
  { id: 'admin', label: 'Admin', blurb: 'Manage team, publish, edit settings.' },
  { id: 'editor', label: 'Editor', blurb: 'Create and publish tours + content.' },
  { id: 'viewer', label: 'Viewer', blurb: 'Read-only + preview drafts.' },
];

export function Members() {
  const { getToken } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [confirmNode, ask] = useConfirm();

  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, i, p] = await Promise.all([
        api('/v1/admin/team'),
        api('/v1/admin/invites').catch(() => []),
        api('/v1/admin/projects').catch(() => ({ items: [] })),
      ]);
      setProjects(p?.items || []);
      // /v1/admin/team returns { members: [...] } — every platform
      // member of this tenant, enriched with Admin.Users data when
      // available and flagged `is_self` on the caller.
      const teamList = t?.members || [];
      setMembers(teamList);
      setInvites(Array.isArray(i) ? i : (i?.items || []));
      // `is_self` flag is reliable here; the caller is guaranteed to
      // be in the list since the endpoint requires admin auth via
      // platform membership.
      const self = teamList.find((m) => m.is_self);
      setMyRole(self?.role || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const canManage = myRole === 'owner' || myRole === 'admin';

  // What needs the admin's attention on this page?  Pending invites
  // they should chase, missing project scoping on members, that sort
  // of thing.  Empty list collapses the strip.
  const actions = useMemo(() => {
    if (!canManage) return [];
    const out = [];
    if (invites.length > 0) {
      out.push({
        id: 'invites',
        title: invites.length === 1 ? '1 outstanding invite' : `${invites.length} outstanding invites`,
        body: 'These haven\'t been accepted yet. Resend the link or revoke if they\'re no longer needed.',
        cta: 'Jump to invites',
        to: '#invites',
        tone: 'normal',
      });
    }
    // Surface members who have *no* project scope set when there's
    // more than one project — easy to forget after creating a second
    // project, and the inbox will still show every project to them.
    const unscoped = members.filter((m) =>
      m.role !== 'owner' && (m.project_ids || []).length === 0
    ).length;
    if (unscoped > 0 && projects.length > 1) {
      out.push({
        id: 'scope',
        title: `${unscoped} member${unscoped === 1 ? ' has' : 's have'} tenant-wide access`,
        body: 'With more than one project, you usually want admins scoped to their own. Edit them below to pick projects.',
        cta: 'Review members',
        to: '#members',
        tone: 'normal',
      });
    }
    return out;
  }, [canManage, invites.length, members, projects.length]);

  return (
    <div className="page">
      <style>{COPYABLE_CODE_CSS}</style>
      <div className="page__title">
        <div>
          <h1>Team</h1>
          <p className="page__subtitle">
            Who can build, publish, and review in your Spotlight tenant.
            Roles stack top-down — Owner includes everything an Admin can
            do, Admin includes everything an Editor can do, and so on.
          </p>
        </div>
      </div>

      {loading && (
        <div className="state">
          <div className="state__title">Loading your team…</div>
        </div>
      )}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load team</div>
          <div>{error}</div>
        </div>
      )}

      {!loading && !error && (
        <>
          <ContextActions actions={actions} />

          {canManage && (
            <InviteForm api={api} onInvited={load} myRole={myRole} />
          )}

          {canManage && <PendingAccessRequests api={api} onChanged={load} />}

          <section className="stack" id="members">
            <h2>Members</h2>
            {members.length === 0 ? (
              <div className="state">
                <div className="state__title">No admin users yet</div>
                <div>Invite a teammate above to get started.</div>
              </div>
            ) : (
              <ul className="list">
                {members.map((m) => (
                  <MemberRow
                    key={m.platform_user_id}
                    member={m}
                    canManage={canManage}
                    myRole={myRole}
                    projects={projects}
                    api={api}
                    onChanged={load}
                    ask={ask}
                  />
                ))}
              </ul>
            )}
          </section>

          {canManage && invites.length > 0 && (
            <section className="stack" id="invites">
              <h2>Outstanding invites</h2>
              <ul className="list">
                {invites.map((i) => (
                  <li key={i.invite_code} className="list__row">
                    <div className="list__primary">
                      <span className="list__name">{i.email}</span>
                      <span className="list__meta">
                        expires {formatDate(i.expires_at)}
                      </span>
                    </div>
                    <div className="row">
                      <InviteDeliveryPill
                        delivered={i.email_delivered}
                        acceptUrl={i.accept_url}
                      />
                      <RoleBadge role={i.role} />
                      <button
                        type="button"
                        className="btn btn--danger"
                        onClick={() => ask({
                          title: `Revoke invite for ${i.email}?`,
                          body: 'The pending invite link stops working immediately.',
                          confirmLabel: 'Revoke',
                          danger: true,
                          runOnConfirm: async () => {
                            await api(`/v1/admin/invites/${i.invite_code}`, {
                              method: 'DELETE',
                            });
                            load();
                          },
                        })}
                      >
                        <DeleteIcon /> Revoke
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {confirmNode}
    </div>
  );
}

function MemberRow({ member, canManage, myRole, projects = [], api, onChanged, ask }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Self-edit always allowed (name).  Role editing limited to admins
  // and above; demoting yourself is allowed, demoting another owner
  // is limited to other owners (enforced server-side too).
  const canEditRole = canManage && !member.is_self;
  const canEditName = canManage || member.is_self;
  const canRemove = canManage && !member.is_self;

  async function save(patch) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/admin/team/${encodeURIComponent(member.platform_user_id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  function remove() {
    ask({
      title: `Remove ${member.display_name || member.email || member.platform_user_id}?`,
      body: (
        <>
          <p>They lose dashboard access immediately.</p>
          <p>
            Any personal API keys they minted on this tenant are
            revoked as part of the removal; system-owned keys
            (project embeds, shared backend integrations) keep
            working.
          </p>
        </>
      ),
      confirmLabel: 'Remove',
      danger: true,
      runOnConfirm: async () => {
        await api(`/v1/admin/team/${encodeURIComponent(member.platform_user_id)}`, {
          method: 'DELETE',
        });
        onChanged();
      },
    });
  }

  if (editing) {
    return (
      <li className="list__row" style={{ display: 'block' }}>
        <EditForm
          member={member}
          myRole={myRole}
          projects={projects}
          canEditRole={canEditRole}
          canEditName={canEditName}
          busy={busy}
          onSubmit={save}
          onCancel={() => { setEditing(false); setErr(null); }}
        />
        {err && <p style={{ color: 'var(--danger-700)', marginTop: 8 }}>{err}</p>}
      </li>
    );
  }

  const label = member.display_name || member.email || member.platform_user_id;
  const meta = [
    member.email || '',
    member.platform_user_id ? member.platform_user_id.slice(0, 12) + '…' : '',
  ].filter(Boolean).join(' · ');

  // Owners always have tenant-wide access regardless of project_ids;
  // show "All projects" rather than "0 projects" so the UI doesn't
  // lie to the operator.
  const isOwner = member.role === 'owner';
  const projectIds = member.project_ids || [];
  const accessLabel = isOwner
    ? 'All projects'
    : projectIds.length === 0
      ? 'All projects'
      : `${projectIds.length} project${projectIds.length === 1 ? '' : 's'}`;

  // Orphan: an Admin.Users row exists but no Platform.Memberships row.
  // Surfaces the "added via POST /v1/admin/users without the matching
  // membership write" case so it's obvious + actionable.  Editing the
  // row (any field) creates the membership server-side.
  const isOrphan = member.has_platform_membership === false && member.has_admin_user_row;

  return (
    <li className="list__row">
      <div className="list__primary">
        <span className="list__name">
          {label}
          {member.is_self && (
            <span className="badge badge--default" style={{ marginLeft: 8 }}>you</span>
          )}
          {isOrphan && (
            <span
              className="badge badge--admin"
              style={{ marginLeft: 8 }}
              title={
                "This user has admin powers in the overlay but hasn't been " +
                "granted dashboard access yet. Click Edit + Save to grant it."
              }
            >
              No dashboard access
            </span>
          )}
        </span>
        {meta && <span className="list__meta">{meta}</span>}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <span
          className="badge badge--default"
          title={
            isOwner
              ? 'Owners see every project automatically.'
              : projectIds.length === 0
                ? 'Tenant-wide access — sees every project.'
                : projectIds.join(', ')
          }
        >
          {accessLabel}
        </span>
        <RoleBadge role={member.role} />
        {(canEditRole || canEditName) && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            onClick={() => setEditing(true)}
          >
            <EditIcon /> Edit
          </button>
        )}
        {canRemove && (
          <button
            type="button"
            className="btn btn--danger"
            disabled={busy}
            onClick={remove}
          >
            <RemoveIcon /> Remove
          </button>
        )}
      </div>
    </li>
  );
}

function EditForm({ member, myRole, projects = [], canEditRole, canEditName, busy, onSubmit, onCancel }) {
  const [displayName, setDisplayName] = useState(member.display_name || '');
  const [email, setEmail] = useState(member.email || '');
  const [role, setRole] = useState(member.role || 'viewer');
  // Local copy of the project allowlist — empty means tenant-wide
  // (which is also what owners always get server-side).  Render as
  // multi-select pills the admin can toggle.
  const [projectIds, setProjectIds] = useState(member.project_ids || []);

  const availableRoles = myRole === 'owner' ? ROLES : ROLES.filter((r) => r.id !== 'owner');
  const accessLocked = role === 'owner';

  function toggleProject(pid) {
    setProjectIds((cur) => (cur.includes(pid) ? cur.filter((p) => p !== pid) : [...cur, pid]));
  }

  function submit(e) {
    e.preventDefault();
    const patch = {};
    if (canEditName && displayName !== (member.display_name || '')) patch.display_name = displayName;
    if (canEditName && email !== (member.email || '')) patch.email = email;
    if (canEditRole && role !== member.role) patch.role = role;
    // Send project_ids whenever the set changed — explicit `[]` resets
    // to tenant-wide, which is the correct semantics for "give them
    // access to everything again".
    const before = (member.project_ids || []).slice().sort().join(',');
    const after = projectIds.slice().sort().join(',');
    if (canManageProjects(canEditRole, role) && before !== after) {
      patch.project_ids = projectIds;
    }
    onSubmit(patch);
  }

  return (
    <form onSubmit={submit} className="member-edit">
      <div className="member-edit__row">
        <label className="field">
          <span className="field__label">Name</span>
          <input
            className="input"
            disabled={!canEditName}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Jane Doe"
          />
        </label>
        <label className="field">
          <span className="field__label">Email</span>
          <input
            className="input"
            type="email"
            disabled={!canEditName}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@yourcompany.com"
          />
        </label>
        <label className="field">
          <span className="field__label">Role</span>
          <select
            className="select"
            disabled={!canEditRole}
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            {availableRoles.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
        </label>
      </div>

      {canEditRole && (
        <div className="member-edit__access">
          <div className="field__label">Project access</div>
          {accessLocked ? (
            <div className="member-edit__note">
              Owners always see every project — project access is managed
              automatically.
            </div>
          ) : projects.length === 0 ? (
            <div className="member-edit__note">
              No projects yet. Create one under <strong>Projects</strong> to
              scope this teammate.
            </div>
          ) : (
            <>
              <div className="member-edit__pills" role="group" aria-label="Projects">
                <button
                  type="button"
                  className={'pill ' + (projectIds.length === 0 ? 'pill--active' : '')}
                  onClick={() => setProjectIds([])}
                  title="Clear the list — gives tenant-wide access (every project, plus tenant-wide threads)."
                >
                  All projects
                </button>
                {projects.map((p) => (
                  <button
                    type="button"
                    key={p.project_id}
                    className={'pill ' + (projectIds.includes(p.project_id) ? 'pill--active' : '')}
                    onClick={() => toggleProject(p.project_id)}
                    title={p.project_id}
                  >
                    {p.name || p.slug || p.project_id}
                  </button>
                ))}
              </div>
              <div className="member-edit__hint">
                Pick the projects they should see in the Help inbox and (later)
                project-scoped tours / content. Leave empty for tenant-wide access.
              </div>
            </>
          )}
        </div>
      )}

      <div className="member-edit__actions">
        <button type="submit" className="btn btn--primary" disabled={busy}>
          <SaveIcon /> {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={onCancel}>
          <XIcon /> Cancel
        </button>
      </div>
    </form>
  );
}

function canManageProjects(canEditRole, role) {
  return canEditRole && role !== 'owner';
}

/**
 * Card-shell + button that launches the 3-step Invite wizard
 * (card 059).  Fetches email-deploy health up-front so step 3 can
 * warn before the operator hits Send.
 */
function InviteForm({ api, onInvited, myRole }) {
  const { showToast } = useToast();
  const [showWizard, setShowWizard] = useState(false);
  const [result, setResult] = useState(null);
  const [emailHealth, setEmailHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api('/v1/admin/email/health')
      .then((h) => { if (!cancelled) setEmailHealth(h); })
      .catch(() => { /* non-fatal — assume configured */ });
    return () => { cancelled = true; };
  }, [api]);

  const availableRoles = myRole === 'owner'
    ? ROLES
    : ROLES.filter((r) => r.id !== 'owner');

  return (
    <section className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div>
          <h3 className="card__title" style={{ margin: 0 }}>Invite a teammate</h3>
          <p className="page__subtitle" style={{ marginTop: 4 }}>
            Generate an invite link and (where email is configured) send
            it directly. Roles stack top-down — pick the lowest one
            that gets the job done.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => { setResult(null); setShowWizard(true); }}
        >
          <SendIcon /> Invite teammate
        </button>
      </div>

      {emailHealth && emailHealth.configured === false && (
        <EmailHealthWarning provider={emailHealth.provider} />
      )}

      {showWizard && (
        <InviteWizard
          api={api}
          availableRoles={availableRoles}
          emailHealth={emailHealth}
          onCancel={() => setShowWizard(false)}
          onSent={(resp) => {
            setShowWizard(false);
            setResult(resp);
            showToast({
              title: resp?.email_delivered
                ? `Invite emailed to ${resp.email}`
                : `Invite generated — copy the link to share`,
              variant: resp?.email_delivered ? 'success' : 'warning',
            });
            onInvited();
          }}
        />
      )}

      {result && <InviteResult result={result} />}
    </section>
  );
}

/**
 * Three-step Invite wizard.
 *
 *   1. Email + role (validated client-side).
 *   2. Custom message (optional) — stored on the wizard for now;
 *      backend does not consume it today but the field is captured
 *      in the draft for when the API gains a `message` body field.
 *   3. Review + Send — surfaces the email-deploy health warning
 *      inline so the operator sees it before clicking Send.
 */
function InviteWizard({ api, availableRoles, emailHealth, onCancel, onSent }) {
  const initial = {
    email: '',
    role: availableRoles.find((r) => r.id === 'editor') ? 'editor' : availableRoles[0]?.id || 'viewer',
    message: '',
  };

  const emailConfigured = emailHealth?.configured !== false;

  return (
    <Wizard
      id="invite-teammate"
      title="Invite a teammate"
      subtitle="Mints an invite link bound to the email + role you pick."
      initialValues={initial}
      submitLabel={emailConfigured ? 'Send invite' : 'Generate link'}
      onCancel={onCancel}
      onSubmit={async (values) => {
        const trimmedMessage = (values.message || '').trim();
        const resp = await api('/v1/admin/invites', {
          method: 'POST',
          body: JSON.stringify({
            email: values.email.trim(),
            role: values.role,
            message: trimmedMessage || undefined,
          }),
        });
        onSent(resp);
      }}
    >
      <WizardStep
        title="Email + role"
        isValid={(v) => /.+@.+\..+/.test(v.email.trim()) && !!v.role}
      >
        {({ values, set }) => (
          <div className="wiz-stack">
            <label className="field">
              <span className="field__label">Email</span>
              <input
                type="email"
                className="input"
                value={values.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="jane@yourcompany.com"
              />
            </label>
            <label className="field">
              <span className="field__label">Role</span>
              <select
                className="select"
                value={values.role}
                onChange={(e) => set('role', e.target.value)}
              >
                {availableRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label} — {r.blurb}
                  </option>
                ))}
              </select>
              <p className="wiz-hint">
                Roles stack — Owner includes everything an Admin can do,
                Admin includes everything an Editor can do, and so on.
              </p>
            </label>
          </div>
        )}
      </WizardStep>

      <WizardStep title="Custom message (optional)">
        {({ values, set }) => (
          <div className="wiz-stack">
            <label className="field">
              <span className="field__label">Message</span>
              <textarea
                className="input"
                rows={4}
                value={values.message}
                onChange={(e) => set('message', e.target.value)}
                placeholder="A line they'll see in the invite email."
              />
              <p className="wiz-hint">
                Optional — leave blank to use the default email body.
              </p>
            </label>
          </div>
        )}
      </WizardStep>

      <WizardStep title="Review + send">
        {({ values }) => (
          <div className="wiz-stack">
            {!emailConfigured && (
              <div
                role="alert"
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: '#fef3c7',
                  border: '1px solid #fcd34d',
                  color: '#78350f',
                  fontSize: 13,
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                }}
              >
                <AlertTriangleIcon />
                <span>
                  <strong>Email isn&apos;t configured for this deploy</strong>
                  {' — '}
                  hitting Send will still mint the invite link, but it
                  won&apos;t be emailed. You&apos;ll need to copy the link from the
                  confirmation banner and share it manually. See your
                  status page for setup.
                </span>
              </div>
            )}
            <dl className="wiz-review">
              <dt>Email</dt>
              <dd>{values.email}</dd>
              <dt>Role</dt>
              <dd>{values.role}</dd>
              {values.message && (
                <>
                  <dt>Message</dt>
                  <dd style={{ whiteSpace: 'pre-wrap' }}>{values.message}</dd>
                </>
              )}
            </dl>
          </div>
        )}
      </WizardStep>
    </Wizard>
  );
}

/**
 * Yellow inline pill rendered above the invite-form email input
 * when GET /v1/admin/email/health reports the deploy isn't wired
 * for outbound mail.  Surfacing this BEFORE submit (rather than
 * after the first failed send) saves the admin a confused round
 * trip and tells them up front that they'll need to copy the
 * accept URL manually.
 */
function EmailHealthWarning({ provider }) {
  return (
    <div
      role="status"
      style={{
        marginTop: 8,
        padding: '8px 12px',
        borderRadius: 8,
        background: '#fef3c7', // amber-100
        border: '1px solid #fcd34d', // amber-300
        color: '#78350f', // amber-900
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span aria-hidden style={{ fontSize: 15 }}>⚠️</span>
      <span>
        <strong>Email isn't configured for this deploy</strong>
        {' — '}
        invites will only generate copyable links.
        {provider === 'noop' && (
          <span style={{ marginLeft: 4, opacity: 0.75 }}>
            (an email-sending key needs to be set on the API for outbound mail to work.)
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * Small status pill for an outstanding invite row.
 *
 *   delivered=true  → green "Email delivered"
 *   delivered=false → amber "Email failed — copy link" with the
 *     copy-button inline so the admin can hand off the link
 *     without leaving the row.
 *
 * Reads from the persisted `email_delivered` on the row so a page
 * refresh after a failed send still shows the right state.
 */
function InviteDeliveryPill({ delivered, acceptUrl }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!acceptUrl) return;
    try {
      const url = acceptUrl.startsWith('http')
        ? acceptUrl
        : `${window.location.origin}${acceptUrl}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — admin still sees the badge */ }
  }

  if (delivered) {
    return (
      <span
        className="badge badge--editor"
        title="The invite email was accepted by the provider."
      >
        Email delivered
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <span
        className="badge badge--admin"
        title="The provider rejected the message (or the deploy has no email key) — share the link manually."
      >
        Email failed
      </span>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={copy}
        style={{ fontSize: 12, padding: '2px 8px' }}
        title="Copy the accept link so you can share it manually."
        aria-label={copied ? 'Copied accept link' : 'Copy accept link'}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
        {copied ? 'Copied' : 'Copy link'}
      </button>
    </span>
  );
}

/**
 * Render the post-submit feedback row for an invite.
 *
 * Three states we surface differently:
 *
 *  * email_delivered=true  — green badge "Email sent" + dim link
 *    (the URL stays available as a fallback but isn't shouted)
 *  * email_delivered=false with email_error — amber notice "we
 *    couldn't email" + prominent link + Copy button
 *  * email_delivered=false with no error — same as the second
 *    case but reads "email sender not configured" (NoopEmailService)
 */
function InviteResult({ result }) {
  // accept_url comes back as an absolute URL when the backend has
  // the tenant's dashboard_url configured (the common case).  If
  // it's still a bare path we lift it onto the current origin
  // so the copy-paste link works for the demo.
  const url = result.accept_url?.startsWith('http')
    ? result.accept_url
    : `${window.location.origin}${result.accept_url || ''}`;

  const sent = result.email_delivered === true;
  // Classify the failure so the banner can name it concretely.
  // Three modes today (see the card body):
  //   * noop — RESEND_API_KEY isn't set on this deploy at all
  //   * resend rejection — Resend returned 4xx (often unverified
  //     From-domain).  The exact reason rides on email_error.
  //   * other transport / unknown — network blip, timeout, etc.
  const errText = (result.email_error || '').toLowerCase();
  let failureMode = 'unknown';
  if (errText.includes('resend_api_key') || errText.startsWith('noop')) {
    failureMode = 'noop';
  } else if (errText.startsWith('resend')) {
    failureMode = 'resend';
  } else if (errText.startsWith('transport')) {
    failureMode = 'transport';
  }

  if (sent) {
    // Success — small inline confirmation (the dashboard has no
    // toast surface, so this acts as the in-page toast the spec
    // asks for).  Still expose the accept_url + copy button so an
    // admin who wants to forward the link manually can.
    return (
      <div
        className="invite-result"
        role="status"
        style={{
          marginTop: 14,
          padding: '10px 12px',
          borderRadius: 8,
          background: '#ecfdf5', // emerald-50
          border: '1px solid #6ee7b7', // emerald-300
          color: '#064e3b', // emerald-900
        }}
      >
        <p style={{ margin: '0 0 6px' }}>
          <span className="badge badge--editor">Email sent</span>{' '}
          Spotlight emailed <strong>{result.email}</strong>.
          The accept link is below if you need to forward it.
        </p>
        <CopyableCode value={url} label="invite link" />
      </div>
    );
  }

  // Failure — render an expanded banner that names the failure
  // mode explicitly + shouts the accept URL so the admin can copy
  // and share manually.  Amber palette to differentiate from the
  // hard-fail red of a request error.
  let title = '';
  let detail = '';
  if (failureMode === 'noop') {
    title = "Email service isn't configured.";
    detail =
      "This deployment doesn't have an email-sending key set, so the " +
      "invite email wasn't sent. Copy the link below and share it " +
      "with the recipient directly.";
  } else if (failureMode === 'resend') {
    title = 'The email service rejected the message.';
    detail = `Reason: ${result.email_error || 'unknown'}. The most ` +
      "common cause is the sender domain not being verified yet. " +
      "Copy the link below and share it manually for now.";
  } else if (failureMode === 'transport') {
    title = "Spotlight couldn't reach the email service.";
    detail = `Network error: ${result.email_error || 'unknown'}. ` +
      'Try sending again in a moment, or copy the link below.';
  } else {
    title = "We couldn't email the invite.";
    detail = `Reason: ${result.email_error || 'unknown'}. ` +
      'Copy the link below and share it with the recipient directly.';
  }

  return (
    <div
      className="invite-result"
      role="alert"
      style={{
        marginTop: 14,
        padding: '12px 14px',
        borderRadius: 8,
        background: '#fffbeb', // amber-50
        border: '1px solid #fcd34d', // amber-300
        color: '#78350f', // amber-900
      }}
    >
      <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
        <span className="badge badge--admin" style={{ marginRight: 8 }}>
          Email not sent
        </span>
        {title}
      </p>
      <p style={{ margin: '0 0 10px', fontSize: 13 }}>{detail}</p>
      <CopyableCode value={url} label="invite link" />
    </div>
  );
}

function RoleBadge({ role }) {
  const cls = ROLES.some((r) => r.id === role) ? `badge--${role}` : 'badge--default';
  return <span className={`badge ${cls}`}>{role}</span>;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Pending access requests — the SDK overlay's "Request access"
 * pane drops rows in `Admin.AccessRequests`; this surfaces them
 * with a role dropdown so an admin can approve at the right
 * level.  Default role is viewer; the user picks higher per
 * request.
 *
 * Renders nothing when the list is empty — keeps the page tidy
 * for the common case.
 */
function PendingAccessRequests({ api, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api('/v1/admin/access-requests?status_filter=pending');
      setItems(resp?.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (error) {
    return (
      <section className="stack">
        <h2>Pending access requests</h2>
        <div className="state state--error">
          <div className="state__title">Couldn't load requests</div>
          <div>{error}</div>
        </div>
      </section>
    );
  }
  if (items.length === 0) return null;

  return (
    <section className="stack">
      <h2>
        Pending access requests{' '}
        <span className="badge badge--admin">{items.length}</span>
      </h2>
      <p className="page__subtitle">
        Someone hit the bookmarklet or the SDK overlay on your site
        and asked to be made an admin. Pick a role and approve, or
        deny with a note. Defaults to <strong>viewer</strong> — bump
        higher per request if you trust them more.
      </p>
      <ul className="list">
        {items.map((req) => (
          <AccessRequestRow
            key={req.request_id}
            request={req}
            api={api}
            onResolved={() => {
              load();
              onChanged?.();
            }}
          />
        ))}
      </ul>
    </section>
  );
}

function AccessRequestRow({ request, api, onResolved }) {
  const [role, setRole] = useState('viewer');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function act(verb) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/admin/access-requests/${request.request_id}/${verb}`, {
        method: 'POST',
        body: JSON.stringify(
          verb === 'approve' ? { role, note } : { note }
        ),
      });
      onResolved?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const displayId = request.requested_email || request.requested_user_id;

  return (
    <li className="list__row list__row--column">
      <div className="row row--spread">
        <div className="list__primary">
          <span className="list__name">{displayId}</span>
          <span className="list__meta">
            {request.email_verified ? (
              <span className="badge badge--editor">verified</span>
            ) : (
              <span className="badge badge--default">unverified</span>
            )}
            {' · '}
            via {request.source}
            {' · '}
            {formatDateTime(request.created_at)}
          </span>
        </div>
        <div className="row">
          <select
            className="select"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={busy}
          >
            {ROLES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy}
            onClick={() => act('approve')}
          >
            <CheckIcon /> Approve
          </button>
          <button
            type="button"
            className="btn btn--secondary"
            disabled={busy}
            onClick={() => act('deny')}
          >
            <XIcon /> Deny
          </button>
        </div>
      </div>
      {request.message ? (
        <div className="list__detail">
          <em>"{request.message}"</em>
        </div>
      ) : null}
      {request.context && Object.keys(request.context).length > 0 && (
        <AccessRequestContext context={request.context} />
      )}
      <div className="row">
        <input
          type="text"
          className="input input--full"
          placeholder="Optional note for the audit log (e.g. 'chatted on Slack first')"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
        />
      </div>
      {err && <div className="state__error">{err}</div>}
    </li>
  );
}

/** Folded snapshot of where the bookmarklet was triggered.
 *
 *  Captured by the SDK at submit time and forwarded to the audit
 *  pane so reviewers see *where* + *who* before clicking Approve —
 *  the origin commonly suggests an obvious Sites allowlist entry,
 *  and the JWT claims tell you whether the requester's host site
 *  already verified them (tenant id, role, plan).  Defaults closed
 *  so the row stays compact; click to expand.
 */
function AccessRequestContext({ context }) {
  const [open, setOpen] = useState(false);
  const page = context.page || {};
  const claims = context.claims || {};
  const origin = page.origin || '';
  // Short summary always visible on the list row — full detail under
  // the expander so a long list of pending requests stays scannable.
  const summary = [
    origin && (
      <span key="origin" className="ar-ctx__chip" title={page.href || origin}>
        from <code>{origin}</code>
      </span>
    ),
    claims.tenant_id && (
      <span key="tenant" className="ar-ctx__chip">
        host tenant <code>{String(claims.tenant_id)}</code>
      </span>
    ),
    claims.role && (
      <span key="role" className="ar-ctx__chip">
        host role <code>{String(claims.role)}</code>
      </span>
    ),
  ].filter(Boolean);
  return (
    <div className="ar-ctx">
      <div className="ar-ctx__head">
        <div className="ar-ctx__summary">
          {summary.length > 0 ? summary : <span style={{ color: 'var(--text-3)' }}>No origin captured</span>}
        </div>
        <button
          type="button"
          className="btn btn--text ar-ctx__toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
          {open ? 'Hide context' : 'Show context'}
        </button>
      </div>
      {open && (
        <dl className="ar-ctx__dl">
          {Object.entries(context).map(([group, val]) => (
            <ContextGroup key={group} label={group} value={val} />
          ))}
        </dl>
      )}
      <style>{`
        .ar-ctx { margin-top: 6px; padding: 8px 10px; background: var(--surface-1, #f8fafc); border-radius: 8px; }
        .ar-ctx__head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .ar-ctx__summary { display: flex; flex-wrap: wrap; gap: 4px 8px; font-size: 12px; color: var(--text-3, #64748b); }
        .ar-ctx__chip { display: inline-flex; align-items: center; gap: 4px; }
        .ar-ctx__chip code { font-size: 11.5px; padding: 1px 5px; background: var(--surface-0, #fff); border: 1px solid var(--border-1, #e5e7eb); border-radius: 4px; }
        .ar-ctx__toggle { margin-left: auto; font-size: 11.5px; }
        .ar-ctx__dl { margin: 8px 0 0; padding: 8px 10px; background: var(--surface-0, #fff); border: 1px solid var(--border-1, #e5e7eb); border-radius: 6px; display: grid; gap: 8px; font-size: 12px; }
        .ar-ctx__group { display: grid; grid-template-columns: max-content 1fr; gap: 4px 12px; }
        .ar-ctx__group dt { color: var(--brand-600, #4f46e5); font-weight: 700; text-transform: lowercase; }
        .ar-ctx__group dd { margin: 0; }
        .ar-ctx__group dd code { font-size: 11.5px; }
        .ar-ctx__rows { display: grid; grid-template-columns: max-content 1fr; gap: 2px 10px; }
        .ar-ctx__rows dt { color: var(--text-3, #64748b); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .ar-ctx__rows dd { margin: 0; overflow-wrap: anywhere; }
      `}</style>
    </div>
  );
}

function ContextGroup({ label, value }) {
  if (value == null) return null;
  const rows = (typeof value === 'object' && !Array.isArray(value))
    ? Object.entries(value)
    : [['value', value]];
  return (
    <div className="ar-ctx__group">
      <dt>{label}</dt>
      <dd>
        <dl className="ar-ctx__rows">
          {rows.map(([k, v]) => (
            <Fragment key={k}>
              <dt>{k}</dt>
              <dd><code>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</code></dd>
            </Fragment>
          ))}
        </dl>
      </dd>
    </div>
  );
}
